import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener } from '@mui/material';
import {
  getLanguageDetails,
  LANGUAGE_DETAILS,
  LanguageOption,
} from '@monorepo/types';
import { useLanguage } from '../../hooks';
import {
  StyledLanguageCaretDownIcon,
  StyledLanguageCountryIcon,
  StyledLanguageDropDown,
  StyledLanguageDropdownContainer,
  StyledLanguageDropdownOption,
  StyledLanguageDropDownSelector,
  StyledLanguageLabel,
  StyledLanguageLabelText,
} from './language-dropdown.styled';

interface LanguageDropdownProps {
  openDownward?: boolean;
  hideLabel?: boolean;
  noBlur?: boolean;
  onLanguageChange?: (languageCode: LanguageOption) => void;
}

export const LanguageDropdown: FC<LanguageDropdownProps> = ({
  openDownward,
  hideLabel,
  noBlur,
  onLanguageChange,
}) => {
  const { i18n } = useTranslation(['hubCommon']);
  const { changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = i18n.resolvedLanguage ?? LanguageOption.ENGLISH;
  const currentLanguageDetails = getLanguageDetails(currentLanguage);

  const filteredLanguageOptions = Object.entries(LANGUAGE_DETAILS).filter(
    ([lang]) => lang !== i18n.language,
  );

  const handleLanguageChange = (languageCode: LanguageOption) => {
    if (onLanguageChange) {
      onLanguageChange(languageCode);
    } else {
      changeLanguage(languageCode);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    if (isOpen) setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <StyledLanguageDropDown>
        <StyledLanguageDropDownSelector
          onClick={toggleDropdown}
          isOpen={isOpen}
          hideLabel={hideLabel}
        >
          <StyledLanguageLabel>
            <StyledLanguageCountryIcon
              code={currentLanguageDetails.countryCode}
            />
            {!hideLabel && (
              <StyledLanguageLabelText>
                {currentLanguageDetails.label}
              </StyledLanguageLabelText>
            )}
          </StyledLanguageLabel>
          <StyledLanguageCaretDownIcon iconName="CaretDown" isOpen={isOpen} />
        </StyledLanguageDropDownSelector>
        {isOpen && (
          <StyledLanguageDropdownContainer
            openDownward={openDownward}
            noBlur={noBlur}
          >
            {filteredLanguageOptions.map(([lang, details]) => (
              <StyledLanguageDropdownOption
                key={lang}
                onClick={() => handleLanguageChange(lang as LanguageOption)}
              >
                <StyledLanguageCountryIcon code={details.countryCode} />
                <StyledLanguageLabelText>
                  {details.label}
                </StyledLanguageLabelText>
              </StyledLanguageDropdownOption>
            ))}
          </StyledLanguageDropdownContainer>
        )}
      </StyledLanguageDropDown>
    </ClickAwayListener>
  );
};
